/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Layout } from '@/widgets/Layout';
import { AllToolsPage } from '@/pages/Home';
import { LegacyToolRedirect } from '@/pages/LegacyToolRedirect';
import { ToolPage } from '@/pages/ToolPage';
import { NotFoundPage } from '@/pages/NotFound';
import { TOOLS } from '@/shared/config/tools';
import landingPages from '@/shared/config/landing-pages.json';
import toolGuides from '@/shared/config/tool-guides.json';
import type { LandingDetails } from '@/components/ToolGuide';

const siteInfoPages = {
  about: React.lazy(() =>
    import('@/pages/SiteInfo').then((pages) => ({ default: pages.AboutPage })),
  ),
  changelog: React.lazy(() =>
    import('@/pages/SiteInfo').then((pages) => ({ default: pages.ChangelogPage })),
  ),
  privacy: React.lazy(() =>
    import('@/pages/SiteInfo').then((pages) => ({ default: pages.PrivacyPage })),
  ),
  terms: React.lazy(() =>
    import('@/pages/SiteInfo').then((pages) => ({ default: pages.TermsPage })),
  ),
};
const PAGE_LOADING_FALLBACK = (
  <div className="py-8 text-sm text-muted-foreground" role="status" aria-live="polite">
    Loading page…
  </div>
);

// Existing Tools
const EnhancedFormatConverterTool = React.lazy(
  () => import('@/components/EnhancedFormatConverterTool'),
);
const CSVToSQLConverterTool = React.lazy(() => import('@/components/CSVToSQLConverterTool'));
const MockApiTool = React.lazy(() => import('@/components/MockApiTool'));
const ScannedPDFConverterTool = React.lazy(() => import('@/components/ScannedPDFConverterTool'));
const CodeConverterTool = React.lazy(() => import('@/components/CodeConverterTool.tsx'));
const Base64Tool = React.lazy(() => import('@/components/Base64Tool'));
const URLTool = React.lazy(() => import('@/components/URLTool'));
const HexTool = React.lazy(() => import('@/components/HexTool'));
const BinaryTool = React.lazy(() => import('@/components/BinaryTool'));
const UUIDTool = React.lazy(() => import('@/components/UUIDTool'));
const HashTool = React.lazy(() => import('@/components/HashTool'));
const QRCodeTool = React.lazy(() => import('@/components/QRCodeTool'));
const TextUtilitiesTool = React.lazy(() => import('@/components/TextUtilitiesTool'));
const SlugGeneratorTool = React.lazy(() => import('@/components/SlugGeneratorTool'));
const CryptoTool = React.lazy(() => import('@/components/CryptoTool'));
const TimestampConverterTool = React.lazy(() => import('@/components/TimestampConverterTool'));
const TimeZoneConverterTool = React.lazy(() => import('@/components/TimeZoneConverterTool'));
const DateCalculatorTool = React.lazy(() => import('@/components/DateCalculatorTool'));
const TimeUnitConverterTool = React.lazy(() => import('@/components/TimeUnitConverterTool'));
const ColorConverterTool = React.lazy(() => import('@/components/ColorConverterTool'));

// New Tools
const ImageManipulationTool = React.lazy(() => import('@/components/ImageManipulationTool'));
const ImageMergerTool = React.lazy(() => import('@/components/ImageMergerTool'));
const ImageEnhancerTool = React.lazy(() => import('@/components/ImageEnhancerTool'));
const FaviconGeneratorTool = React.lazy(() => import('@/components/FaviconGeneratorTool'));
const SVGOptimizerTool = React.lazy(() => import('@/components/SVGOptimizerTool'));
const MarkdownTool = React.lazy(() => import('@/components/MarkdownTool'));
const PlaceholderGeneratorTool = React.lazy(() => import('@/components/PlaceholderGeneratorTool'));
const ASCIIArtTool = React.lazy(() => import('@/components/ASCIIArtTool'));
const SQLFormatterTool = React.lazy(() => import('@/components/SQLFormatterTool'));
const CSVToolEnhanced = React.lazy(() => import('@/components/CSVToolEnhanced'));
const GitUtilitiesTool = React.lazy(() => import('@/components/GitUtilitiesTool'));
const JSONBeautifierTool = React.lazy(() => import('@/components/JSONBeautifierTool'));
const CodeFormatterTool = React.lazy(() => import('@/components/CodeFormatterTool'));
const NumberBaseConverterTool = React.lazy(() => import('@/components/NumberBaseConverterTool'));
const JWTToolEnhanced = React.lazy(() => import('@/components/JWTToolEnhanced'));
const APIRequestBuilderTool = React.lazy(() => import('@/components/APIRequestBuilderTool'));
const WebSocketTesterTool = React.lazy(() => import('@/components/WebSocketTesterTool'));
const ColorPaletteTool = React.lazy(() => import('@/components/ColorPaletteTool'));
const MockDataGeneratorTool = React.lazy(() => import('@/components/MockDataGeneratorTool'));

// Missing Tools - Now Restored
const DiffCheckerTool = React.lazy(() => import('@/components/DiffCheckerTool'));
const WordCounterTool = React.lazy(() => import('@/components/WordCounterTool'));
const HTMLEntitiesTool = React.lazy(() => import('@/components/HTMLEntitiesTool'));
const StringEscaperTool = React.lazy(() => import('@/components/StringEscaperTool'));
const RegexTesterTool = React.lazy(() => import('@/components/RegexTesterTool'));
const PasswordGeneratorTool = React.lazy(() => import('@/components/PasswordGeneratorTool'));
const SSLCertGenTool = React.lazy(() => import('@/components/SSLCertGenTool'));
const FileEncryptionTool = React.lazy(() => import('@/components/FileEncryptionTool'));
const DNSLookupTool = React.lazy(() => import('@/components/DNSLookupTool'));
const HTMLPreviewTool = React.lazy(() => import('@/components/HTMLPreviewTool'));

// High-Demand New Tools
const CurlToCodeTool = React.lazy(() => import('@/components/CurlToCodeTool'));
const ChmodCalculatorTool = React.lazy(() => import('@/components/ChmodCalculatorTool'));
const OpenGraphPreviewTool = React.lazy(() => import('@/components/OpenGraphPreviewTool'));
const SVGToReactTool = React.lazy(() => import('@/components/SVGToReactTool'));
const SubnetCalculatorTool = React.lazy(() => import('@/components/SubnetCalculatorTool'));
const BcryptGeneratorTool = React.lazy(() => import('@/components/BcryptGeneratorTool'));
const MarkdownTableTool = React.lazy(() => import('@/components/MarkdownTableTool'));
const CronParserTool = React.lazy(() => import('@/components/CronParserTool'));
const JSONSchemaTool = React.lazy(() => import('@/components/JSONSchemaTool'));
const DockerRunToComposeTool = React.lazy(() => import('@/components/DockerRunToComposeTool'));
const JSONToTypeScriptTool = React.lazy(() => import('@/components/JSONToTypeScriptTool'));
const CSSToTailwindTool = React.lazy(() => import('@/components/CSSToTailwindTool'));
const SSHKeyGeneratorTool = React.lazy(() => import('@/components/SSHKeyGeneratorTool'));
const ColorContrastTool = React.lazy(() => import('@/components/ColorContrastTool'));
const HtpasswdGeneratorTool = React.lazy(() => import('@/components/HtpasswdGeneratorTool'));
const TimezoneMeetingPlannerTool = React.lazy(
  () => import('@/components/TimezoneMeetingPlannerTool'),
);
const GitCommandGeneratorTool = React.lazy(() => import('@/components/GitCommandGeneratorTool'));
const RegexVisualizerTool = React.lazy(() => import('@/components/RegexVisualizerTool'));
const UserAgentParserTool = React.lazy(() => import('@/components/UserAgentParserTool'));
const FancyFontTool = React.lazy(() => import('@/components/FancyFontTool'));
const InstagramLineBreakerTool = React.lazy(() => import('@/components/InstagramLineBreakerTool'));
const AspectRatioCalculatorTool = React.lazy(
  () => import('@/components/AspectRatioCalculatorTool'),
);
const TwitterThreadSplitterTool = React.lazy(
  () => import('@/components/TwitterThreadSplitterTool'),
);
const OGImageExtractorTool = React.lazy(() => import('@/components/OGImageExtractorTool'));
const LLMTokenCounterTool = React.lazy(() => import('@/components/LLMTokenCounterTool'));
const CodebaseToPromptTool = React.lazy(() => import('@/components/CodebaseToPromptTool'));
const SystemPromptBuilderTool = React.lazy(() => import('@/components/SystemPromptBuilderTool'));
const MidjourneyPromptBuilderTool = React.lazy(
  () => import('@/components/MidjourneyPromptBuilderTool'),
);
const LLMToolBuilderTool = React.lazy(() => import('@/components/LLMToolBuilderTool'));
const LLMResponseCleanerTool = React.lazy(() => import('@/components/LLMResponseCleanerTool'));
const PromptEnhancerTool = React.lazy(() => import('@/components/PromptEnhancerTool'));
const OpenAPIToPostmanTool = React.lazy(() => import('@/components/OpenAPIToPostmanTool'));
const WhatIsMyIPTool = React.lazy(() => import('@/components/WhatIsMyIPTool'));
const SpeedTestTool = React.lazy(() => import('@/components/SpeedTestTool'));
const AICompatibilityTool = React.lazy(() => import('@/components/AICompatibilityTool'));
const InternetStabilityTool = React.lazy(() => import('@/components/InternetStabilityTool'));
const HTTPHeadersTool = React.lazy(() => import('@/components/HTTPHeadersTool'));
const PixelArtTool = React.lazy(() => import('@/components/PixelArtTool'));
const PDFTools = React.lazy(() => import('@/components/PDFTools'));

// Hardware & Device Tests
const DeadPixelTestTool = React.lazy(() => import('@/components/DeadPixelTestTool'));
const KeyboardTesterTool = React.lazy(() => import('@/components/KeyboardTesterTool'));
const MouseTesterTool = React.lazy(() => import('@/components/MouseTesterTool'));
const TouchscreenTesterTool = React.lazy(() => import('@/components/TouchscreenTesterTool'));
const GamepadTesterTool = React.lazy(() => import('@/components/GamepadTesterTool'));
const SpeakerTestTool = React.lazy(() => import('@/components/SpeakerTestTool'));
const MicrophoneTestTool = React.lazy(() => import('@/components/MicrophoneTestTool'));
const WebcamTestTool = React.lazy(() => import('@/components/WebcamTestTool'));
const RefreshRateTestTool = React.lazy(() => import('@/components/RefreshRateTestTool'));
const SensorTestTool = React.lazy(() => import('@/components/SensorTestTool'));
const BatteryStatusTool = React.lazy(() => import('@/components/BatteryStatusTool'));
const CPUBenchmarkTool = React.lazy(() => import('@/components/CPUBenchmarkTool'));
const MemoryBenchmarkTool = React.lazy(() => import('@/components/MemoryBenchmarkTool'));
const GPUBenchmarkTool = React.lazy(() => import('@/components/GPUBenchmarkTool'));
const DiskSpeedTestTool = React.lazy(() => import('@/components/DiskSpeedTestTool'));

export const TOOL_COMPONENTS: Record<string, React.ComponentType> = {
  'format-converter': EnhancedFormatConverterTool,
  'csv-to-sql': CSVToSQLConverterTool,
  'mock-api': MockApiTool,
  'scanned-pdf-converter': ScannedPDFConverterTool,
  'code-converter': CodeConverterTool,
  base64: Base64Tool,
  url: URLTool,
  hex: HexTool,
  binary: BinaryTool,
  uuid: UUIDTool,
  hash: HashTool,
  qrcode: QRCodeTool,
  'text-utils': TextUtilitiesTool,
  slug: SlugGeneratorTool,
  crypto: CryptoTool,
  'image-converter': ImageManipulationTool,
  timestamp: TimestampConverterTool,
  'time-zone-converter': TimeZoneConverterTool,
  'date-calculator': DateCalculatorTool,
  'time-unit-converter': TimeUnitConverterTool,
  color: ColorConverterTool,
  'image-merger': ImageMergerTool,
  'image-enhancer': ImageEnhancerTool,
  'favicon-generator': FaviconGeneratorTool,
  'svg-optimizer': SVGOptimizerTool,
  markdown: MarkdownTool,
  placeholder: PlaceholderGeneratorTool,
  'ascii-art': ASCIIArtTool,
  'json-schema': JSONSchemaTool,
  'sql-formatter': SQLFormatterTool,
  'csv-enhanced': CSVToolEnhanced,
  'git-utils': GitUtilitiesTool,
  'json-beautifier': JSONBeautifierTool,
  'code-formatter': CodeFormatterTool,
  'number-base': NumberBaseConverterTool,
  'cron-parser': CronParserTool,
  'jwt-enhanced': JWTToolEnhanced,
  'api-request': APIRequestBuilderTool,
  websocket: WebSocketTesterTool,
  'color-palette': ColorPaletteTool,
  'mock-data': MockDataGeneratorTool,
  'diff-checker': DiffCheckerTool,
  'word-counter': WordCounterTool,
  'html-entities': HTMLEntitiesTool,
  'string-escaper': StringEscaperTool,
  'regex-tester': RegexTesterTool,
  'password-generator': PasswordGeneratorTool,
  'ssl-cert-gen': SSLCertGenTool,
  'file-encryption': FileEncryptionTool,
  'dns-lookup': DNSLookupTool,
  'html-preview': HTMLPreviewTool,
  'curl-to-code': CurlToCodeTool,
  'chmod-calculator': ChmodCalculatorTool,
  'opengraph-preview': OpenGraphPreviewTool,
  'svg-to-react': SVGToReactTool,
  'subnet-calculator': SubnetCalculatorTool,
  'bcrypt-generator': BcryptGeneratorTool,
  'markdown-table-generator': MarkdownTableTool,
  'docker-run-to-compose': DockerRunToComposeTool,
  'json-to-typescript': JSONToTypeScriptTool,
  'css-to-tailwind': CSSToTailwindTool,
  'ssh-key-generator': SSHKeyGeneratorTool,
  'color-contrast-checker': ColorContrastTool,
  'htpasswd-generator': HtpasswdGeneratorTool,
  'timezone-meeting-planner': TimezoneMeetingPlannerTool,
  'git-command-generator': GitCommandGeneratorTool,
  'regex-visualizer': RegexVisualizerTool,
  'user-agent-parser': UserAgentParserTool,
  'fancy-font-generator': FancyFontTool,
  'instagram-line-breaker': InstagramLineBreakerTool,
  'aspect-ratio-calculator': AspectRatioCalculatorTool,
  'twitter-thread-splitter': TwitterThreadSplitterTool,
  'og-image-extractor': OGImageExtractorTool,
  'llm-token-counter': LLMTokenCounterTool,
  'codebase-to-prompt': CodebaseToPromptTool,
  'system-prompt-builder': SystemPromptBuilderTool,
  'midjourney-prompt-builder': MidjourneyPromptBuilderTool,
  'llm-tool-builder': LLMToolBuilderTool,
  'llm-response-cleaner': LLMResponseCleanerTool,
  'prompt-enhancer': PromptEnhancerTool,
  'openapi-to-postman': OpenAPIToPostmanTool,
  'what-is-my-ip': WhatIsMyIPTool,
  'speed-test': SpeedTestTool,
  'ai-compatibility-checker': AICompatibilityTool,
  'internet-stability-test': InternetStabilityTool,
  'http-headers-inspector': HTTPHeadersTool,
  'pixel-art-converter': PixelArtTool,
  'pdf-tools': PDFTools,
  'dead-pixel-test': DeadPixelTestTool,
  'keyboard-tester': KeyboardTesterTool,
  'mouse-tester': MouseTesterTool,
  'touchscreen-tester': TouchscreenTesterTool,
  'gamepad-tester': GamepadTesterTool,
  'speaker-test': SpeakerTestTool,
  'microphone-test': MicrophoneTestTool,
  'webcam-test': WebcamTestTool,
  'refresh-rate-test': RefreshRateTestTool,
  'sensor-test': SensorTestTool,
  'battery-status': BatteryStatusTool,
  'cpu-benchmark': CPUBenchmarkTool,
  'memory-benchmark': MemoryBenchmarkTool,
  'gpu-benchmark': GPUBenchmarkTool,
  'disk-speed-test': DiskSpeedTestTool,
};

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<LegacyToolRedirect />} />
        <Route path="all-tools" element={<AllToolsPage />} />
        <Route
          path="about"
          element={
            <React.Suspense fallback={PAGE_LOADING_FALLBACK}>
              <siteInfoPages.about />
            </React.Suspense>
          }
        />
        <Route
          path="privacy"
          element={
            <React.Suspense fallback={PAGE_LOADING_FALLBACK}>
              <siteInfoPages.privacy />
            </React.Suspense>
          }
        />
        <Route
          path="terms"
          element={
            <React.Suspense fallback={PAGE_LOADING_FALLBACK}>
              <siteInfoPages.terms />
            </React.Suspense>
          }
        />
        <Route
          path="changelog"
          element={
            <React.Suspense fallback={PAGE_LOADING_FALLBACK}>
              <siteInfoPages.changelog />
            </React.Suspense>
          }
        />

        {TOOLS.map((tool) => {
          const Component = TOOL_COMPONENTS[tool.id];
          return (
            <Route
              key={tool.id}
              path={tool.path.slice(1)}
              element={
                Component ? (
                  <ToolPage
                    toolId={tool.id}
                    component={Component}
                    landing={(toolGuides as Record<string, LandingDetails>)[tool.id]}
                  />
                ) : (
                  <NotFoundPage />
                )
              }
            />
          );
        })}

        {landingPages.map((landing) => {
          const Component = TOOL_COMPONENTS[landing.toolId];
          return (
            <Route
              key={landing.path}
              path={landing.path.slice(1)}
              element={
                Component ? (
                  <ToolPage
                    toolId={landing.toolId}
                    component={Component}
                    pageTitle={landing.title}
                    pageDescription={landing.description}
                    landing={landing}
                  />
                ) : (
                  <NotFoundPage />
                )
              }
            />
          );
        })}

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export function AppProviders({ children }: React.PropsWithChildren) {
  return <HelmetProvider>{children}</HelmetProvider>;
}

export default function App() {
  return (
    <AppProviders>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AppProviders>
  );
}
